import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import {
    $convertFromMarkdownString,
    $convertToMarkdownString,
    ElementTransformer,
    HEADING,
    TEXT_FORMAT_TRANSFORMERS,
} from "@lexical/markdown";
import {
    $createTextNode,
    $getRoot,
    $insertNodes,
    $setSelection,
    LexicalEditor,
    LexicalNode,
    ParagraphNode,
} from "lexical";

export function lexicalToText(editor: LexicalEditor) {
    return editor.getEditorState().read(() => {
        return $getRoot().getTextContent();
    });
}

// replace this with setEditorContentsFromText
export function lexicalJSONFromText(text: string) {
    return JSON.stringify({
        root: {
            children: [
                {
                    children: [
                        {
                            detail: 0,
                            format: 0,
                            mode: "normal",
                            style: "",
                            text,
                            type: "text",
                            version: 1,
                        },
                    ],
                    direction: null,
                    format: "",
                    indent: 0,
                    type: "paragraph",
                    version: 1,
                },
            ],
            direction: null,
            format: "",
            indent: 0,
            type: "root",
            version: 1,
        },
    });
}

export function lexicalToHtml(editor: LexicalEditor) {
    return editor.getEditorState().read(() => {
        return $generateHtmlFromNodes(editor);
    });
}

export function setEditorContentsFromText(editor: LexicalEditor, text: string, isPlainText = false) {
    editor.update(() => {
        let nodes: Array<LexicalNode>;

        if (isPlainText) {
            nodes = [$createTextNode(text)];
        } else {
            const parser = new DOMParser();
            const dom = parser.parseFromString(text, "text/html");
            nodes = $generateNodesFromDOM(editor, dom);
        }

        $getRoot().clear();
        $insertNodes(nodes);
    });
}

export function escapeCustomTags(input: string, ignoreTags: string[] | undefined = []) {
    return input.replace(/<([\/]?)(\w+)([^>]*)>/g, (match, slash, tagName, attributes) => {
        const hasIgnoredTags = ignoreTags.map((tag) => tag.toLowerCase()).includes(tagName.toLowerCase());
        if (hasIgnoredTags) {
            return match;
        }

        if (slash) {
            return `&lt;/${tagName}${attributes}&gt;`;
        } else {
            if (attributes.trim().endsWith("/")) {
                return `&lt;${tagName}${attributes.slice(0, -1)} /&gt;`;
            } else {
                return `&lt;${tagName}${attributes}&gt;`;
            }
        }
    });
}

/**
 * Unescapes HTML-escaped XML/HTML tags in text content.
 * Restores tags that were escaped to prevent DOMParser from stripping them.
 * Handles tags with hyphens and attributes containing HTML entities like &amp;
 */
export function unescapeAllTags(input: string): string {
    // Match &lt;, tag name (with hyphens), attributes (including &amp; entities), and &gt;
    // The attribute pattern uses negative lookahead to match & only when not followed by gt;
    return input.replace(/&lt;(\/?)([\w-]+)((?:[^&]|&(?!gt;))*)&gt;/g, "<$1$2$3>");
}

/** Preserves blank lines when importing markdown into Lexical (a line with only a space). */
export const PRESERVE_NEWLINES: ElementTransformer = {
    type: "element",
    dependencies: [ParagraphNode],
    export: () => {
        return null;
    },
    regExp: /^$/,
    replace: (textNode, nodes, _, isImport) => {
        if (isImport && nodes.length === 1) {
            textNode.append($createTextNode(""));
        }
    },
};

// Transformers for the elements the prompt editors support (headings + inline text formats).
export const MARKDOWN_TRANSFORMERS = [HEADING, ...TEXT_FORMAT_TRANSFORMERS];

/**
 * Hydrates a Lexical editor from a markdown string. Collapses double newlines to single and
 * clears the selection so the editor does not auto-scroll/focus when mounted.
 */
export function hydrateEditorFromMarkdown(editor: LexicalEditor, markdown: string): void {
    editor.update(() => {
        const interm = markdown.replace(/\n\n/g, "\n");
        $convertFromMarkdownString(interm, [PRESERVE_NEWLINES, ...MARKDOWN_TRANSFORMERS]);
        $setSelection(null);
    });
}

/**
 * Serializes the current Lexical editor state to a markdown string, unescaping the HTML/XML-like
 * tags that `$convertToMarkdownString` escapes (e.g. `&lt;tag&gt;` back to `<tag>`).
 */
export function serializeEditorToMarkdown(editor: LexicalEditor): string {
    const rawValue = editor.getEditorState().read(() => $convertToMarkdownString(MARKDOWN_TRANSFORMERS));
    return rawValue ? unescapeAllTags(rawValue) : "";
}
